<template>
  <div class="lp-page contact-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ $t('contact.heroLabel') }}</span>
        <h1 class="lp-title">{{ $t('contact.heroTitle') }}</h1>
        <p class="lp-lead">{{ $t('contact.heroSubtitle') }}</p>
      </div>
    </section>

    <!-- Content -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="contact-grid">
          <!-- Form -->
          <div class="form-section reveal-item">
            <h2 class="lp-h3">{{ $t('contact.formTitle') }}</h2>
            <form @submit.prevent="handleSubmit" class="contact-form">
              <div class="form-group">
                <label for="name">{{ $t('contact.formName') }} *</label>
                <input type="text" id="name" v-model="form.name" :placeholder="$t('contact.formNamePlaceholder')" required />
              </div>
              <div class="form-group">
                <label for="email">{{ $t('contact.formEmail') }} *</label>
                <input type="email" id="email" v-model="form.email" :placeholder="$t('contact.formEmailPlaceholder')" required />
              </div>
              <div class="form-group">
                <label for="subject">{{ $t('contact.formSubject') }} *</label>
                <input type="text" id="subject" v-model="form.subject" :placeholder="$t('contact.formSubjectPlaceholder')" required />
              </div>
              <div class="form-group">
                <label for="message">{{ $t('contact.formMessage') }} *</label>
                <textarea id="message" v-model="form.message" :placeholder="$t('contact.formMessagePlaceholder')" rows="6" required></textarea>
              </div>
              <button type="submit" class="lp-btn lp-btn-primary btn-submit" :disabled="isSubmitting">
                <font-awesome-icon v-if="isSubmitting" icon="fa-solid fa-spinner" spin />
                <font-awesome-icon v-else icon="fa-solid fa-paper-plane" />
                {{ isSubmitting ? $t('contact.formSending') : $t('contact.formSubmit') }}
              </button>
            </form>
          </div>

          <!-- Info -->
          <div class="info-section reveal-item">
            <h2 class="lp-h3">{{ $t('contact.infoTitle') }}</h2>
            <div class="info-cards">
              <div class="info-card">
                <span class="lp-ico blue"><font-awesome-icon icon="fa-solid fa-envelope" /></span>
                <div class="info-content">
                  <h3>{{ $t('contact.infoEmailTitle') }}</h3>
                  <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>
                </div>
              </div>
              <div class="info-card">
                <span class="lp-ico green"><font-awesome-icon icon="fa-solid fa-phone" /></span>
                <div class="info-content">
                  <h3>{{ $t('contact.infoPhoneTitle') }}</h3>
                  <a href="tel:+359882814412">+359 882 814 412</a>
                  <a href="tel:+359895725972">+359 895 725 972</a>
                </div>
              </div>
              <div class="info-card">
                <span class="lp-ico violet"><font-awesome-icon icon="fa-solid fa-clock" /></span>
                <div class="info-content">
                  <h3>{{ $t('contact.infoHoursTitle') }}</h3>
                  <p>{{ $t('contact.infoWeekdays') }}</p>
                  <p>{{ $t('contact.infoWeekends') }}</p>
                </div>
              </div>
            </div>

            <div class="faq-box">
              <span class="lp-ico amber"><font-awesome-icon icon="fa-solid fa-circle-question" /></span>
              <div>
                <h3>{{ $t('contact.faqTitle') }}</h3>
                <p class="lp-text">{{ $t('contact.faqDescription') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Map -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="map-container reveal-item">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2932.4!2d23.3219!3d42.6977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDQxJzUxLjgiTiAyM8KwMTknMTguOCJF!5e0!3m2!1sen!2sbg!4v1234567890"
            width="100%" height="413" style="border:0;" allowfullscreen="" loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toast-notification';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import axios from 'axios';
import { useReveal } from '../composables/useReveal';

const toast = useToast();

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

useReveal();

const form = ref({ name: '', email: '', subject: '', message: '' });
const isSubmitting = ref(false);

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    const response = await axios.post('/api/contact', form.value);
    if (response.data.success) {
      toast.success(response.data.message);
      form.value = { name: '', email: '', subject: '', message: '' };
    } else {
      toast.error(response.data.message || 'Something went wrong. Please try again.');
    }
  } catch (error) {
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = Object.values(errors)[0][0];
      toast.error(firstError);
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error('Something went wrong. Please try again or contact us at invoicescan.eu@gmail.com');
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.contact-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 32px; align-items: start; }
.form-section, .info-section { background: var(--card); border: 1px solid var(--line); border-radius: var(--radius); padding: 34px 32px; box-shadow: 0 26px 60px -40px rgba(31,41,51,0.3); }
.form-section .lp-h3, .info-section .lp-h3 { margin-bottom: 22px; }

.contact-form { display: flex; flex-direction: column; gap: 18px; }
.form-group { display: flex; flex-direction: column; gap: 7px; }
.form-group label { font-size: 13.5px; font-weight: 600; color: var(--ink); }
.form-group input, .form-group textarea {
  width: 100%; padding: 13px 16px; border: 1px solid var(--line); border-radius: 12px;
  background: #fbfbfd; color: var(--ink); font-size: 15px; font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease; resize: vertical;
}
.form-group input:focus, .form-group textarea:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(43,130,240,0.12); background: #fff; }
.form-group input::placeholder, .form-group textarea::placeholder { color: #aab1bd; }
.btn-submit { width: 100%; margin-top: 6px; }
.btn-submit:disabled { opacity: 0.7; cursor: default; }

.info-cards { display: flex; flex-direction: column; gap: 16px; }
.info-card { display: flex; gap: 16px; align-items: flex-start; }
.info-card .lp-ico { margin-bottom: 0; width: 48px; height: 48px; font-size: 18px; flex-shrink: 0; }
.info-content h3 { font-size: 15px; font-weight: 700; color: var(--ink); margin: 0 0 4px; }
.info-content a, .info-content p { display: block; color: var(--muted); font-size: 14px; text-decoration: none; margin: 1px 0; }
.info-content a:hover { color: var(--violet); }

.faq-box { display: flex; gap: 16px; align-items: flex-start; margin-top: 24px; padding-top: 24px; border-top: 1px solid var(--line); }
.faq-box .lp-ico { margin-bottom: 0; width: 48px; height: 48px; font-size: 18px; flex-shrink: 0; }
.faq-box h3 { font-size: 15px; font-weight: 700; color: var(--ink); margin: 0 0 4px; }

.map-container { border-radius: var(--radius); overflow: hidden; border: 1px solid var(--line); box-shadow: var(--shadow); }
.map-container iframe { display: block; }

@media (max-width: 1024px) {
  .contact-grid { grid-template-columns: 1fr; }
}
</style>
